import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { ProductivityNotification } from './productivity.model';
import { ProductivityService } from './productivity.service';

@Component({
  selector: 'app-productivity-notifications',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, DatePipe, ButtonModule, TableModule, TagModule, ToggleSwitchModule, PageShellComponent, PageHeroComponent, PageDataPanelComponent],
  template: `
    <app-page-shell>
      <app-page-hero
        eyebrow="Workspace"
        title="Notifications"
        subtitle="Inbox for workflow and productivity updates across the operating workspace.">
        <div actions>
          <label class="flex align-items-center gap-2 text-sm text-600">
            <p-toggleSwitch [(ngModel)]="unreadOnly" (onChange)="load()"></p-toggleSwitch>
            Unread only
          </label>
          <p-button label="Mark all read" icon="pi pi-check" severity="secondary" (onClick)="markAllRead()"></p-button>
        </div>
      </app-page-hero>

      <app-page-data-panel title="Notification Inbox" subtitle="Unread items are highlighted for quick triage." [resultText]="notifications.length + ' notifications'">
        <p-table [value]="notifications" [loading]="loading" [paginator]="true" [rows]="15" styleClass="workspace-table">
          <ng-template pTemplate="header">
            <tr><th>Notification</th><th>Severity</th><th>Created</th><th class="text-right">Action</th></tr>
          </ng-template>
          <ng-template pTemplate="body" let-item>
            <tr [class.surface-50]="!item.readFlag">
              <td>
                <div class="font-semibold">{{ item.title }}</div>
                <div class="text-sm text-500">{{ item.message }}</div>
                <div class="text-xs text-500 mt-1" *ngIf="item.resourceKey">{{ item.resourceKey }} #{{ item.resourceId }}</div>
              </td>
              <td><p-tag [value]="item.severity || 'INFO'" [severity]="severity(item.severity)"></p-tag></td>
              <td>{{ item.createdAt | date:'medium' }}</td>
              <td class="text-right">
                <p-button *ngIf="item.actionUrl" icon="pi pi-external-link" [text]="true" [routerLink]="item.actionUrl"></p-button>
                <p-button *ngIf="!item.readFlag" icon="pi pi-check" [text]="true" (onClick)="markRead(item)"></p-button>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="4" class="text-center p-4 text-500">No notifications found.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>
    </app-page-shell>
  `,
  styles: [`
    :host { display: block; }
    :host ::ng-deep .workspace-table .p-datatable-table { min-width: 48rem; }
  `]
})
export class ProductivityNotificationsComponent implements OnInit {
  private service = inject(ProductivityService);
  notifications: ProductivityNotification[] = [];
  loading = false;
  unreadOnly = false;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.service.getNotifications(this.unreadOnly).subscribe({
      next: res => {
        this.notifications = res.data || [];
        this.loading = false;
      },
      error: () => this.loading = false
    });
  }

  markRead(item: ProductivityNotification): void {
    if (!item.id) return;
    this.service.markNotificationRead(item.id).subscribe(() => {
      item.readFlag = true;
      this.service.refreshSummary();
      if (this.unreadOnly) this.load();
    });
  }

  markAllRead(): void {
    this.service.markAllNotificationsRead().subscribe(() => {
      this.service.refreshSummary();
      this.load();
    });
  }

  severity(value?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    if (value === 'ERROR' || value === 'CRITICAL') return 'danger';
    if (value === 'WARN' || value === 'WARNING') return 'warn';
    if (value === 'SUCCESS') return 'success';
    return 'info';
  }
}
